import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Scale, Loader2, CheckCircle, XCircle, Mail } from 'lucide-react';
import { api } from '../lib/api';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setLoading(false);
      setError('No verification token provided');
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await api.post(`/auth/verify-email/${token}`);
      setSuccess(true);
      
      // Store token and update user
      if (response.data.access_token) {
        localStorage.setItem('fundfair_token', response.data.access_token);
        updateUser(response.data.user);
        toast.success('Email verified! Redirecting...');
        setTimeout(() => navigate('/discover'), 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed');
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-background to-secondary/50 flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
            <Scale className="w-6 h-6 text-primary-foreground" />
          </div>
          <span className="text-2xl font-bold text-foreground tracking-tight">
            Fund<span className="text-primary">fair</span>
          </span>
        </div>

        <Card className="border-border/50 shadow-xl">
          <CardHeader className="space-y-1 text-center pb-4">
            <CardTitle className="text-2xl">Email Verification</CardTitle>
            <CardDescription>
              {loading
                ? "Verifying your email..."
                : success
                ? "Your email has been verified!"
                : "Verification failed"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex flex-col items-center py-8">
                <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">Please wait while we verify your email...</p>
              </div>
            ) : success ? (
              <div className="flex flex-col items-center py-4">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <p className="text-center text-muted-foreground mb-6">
                  Your email has been verified successfully. You will be redirected to the app shortly.
                </p>
                <Link to="/discover">
                  <Button className="rounded-full">
                    Go to App
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="flex flex-col items-center py-4">
                <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-4">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
                <p className="text-center text-muted-foreground mb-2">
                  {error}
                </p>
                <p className="text-center text-sm text-muted-foreground mb-6">
                  The verification link may have expired or is invalid.
                </p>
                <div className="flex gap-3">
                  <Link to="/login">
                    <Button variant="outline" className="rounded-full">
                      Back to Login
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
